import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DOCUMENT_TYPES, MAX_DOCUMENT_BYTES } from '@guabi/shared';
import { Button } from '@/components/ui/Button';
import { ApiRequestError } from '@/lib/api';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { openDocument, useDeleteDocument, useDocuments, useUploadDocument } from './queries';

/**
 * The contracts and invoices attached to one thing.
 *
 * Lives inside the vendor and venue sheets rather than on a tab of its own: a
 * contract is only ever meaningful next to whoever you signed it with, and a
 * flat list of twelve PDFs is a filing cabinet, not a wedding planner.
 */
export function Documents({
  wedId,
  vendorId,
  venueId,
}: {
  wedId: string;
  vendorId?: string;
  venueId?: string;
}) {
  const { t } = useTranslation();
  const documents = useDocuments(wedId);
  const upload = useUploadDocument(wedId);
  const remove = useDeleteDocument(wedId);
  const input = useRef<HTMLInputElement>(null);
  const [error, setError] = useState<string>();
  const [pendingRemove, setPendingRemove] = useState<{ docId: string; filename: string }>();

  const mine = (documents.data?.items ?? []).filter((d) =>
    vendorId ? d.vendorId === vendorId : venueId ? d.venueId === venueId : false,
  );
  // A row with no `uploadedAt` is a presign nobody finished. Shown, not hidden,
  // so a failed upload is something you can clear rather than a mystery.
  const done = mine.filter((d) => d.uploadedAt);
  const unfinished = mine.filter((d) => !d.uploadedAt);

  async function onPick(file: File) {
    setError(undefined);
    if (file.size > MAX_DOCUMENT_BYTES) {
      setError(t('documents.tooBig'));
      return;
    }
    if (!DOCUMENT_TYPES.includes(file.type as (typeof DOCUMENT_TYPES)[number])) {
      setError(t('documents.wrongType'));
      return;
    }
    try {
      await upload.mutateAsync({
        file,
        ...(vendorId ? { vendorId } : {}),
        ...(venueId ? { venueId } : {}),
      });
    } catch (err) {
      setError(err instanceof ApiRequestError ? err.message : t('documents.uploadFailed'));
    } finally {
      if (input.current) input.current.value = '';
    }
  }

  return (
    <div className="flex flex-col gap-3">
      <h3 className="text-section">{t('documents.heading')}</h3>

      {done.length === 0 && unfinished.length === 0 ? (
        <p className="text-caption text-leaf-soft">{t('documents.none')}</p>
      ) : (
        <ul className="flex flex-col divide-y divide-line">
          {done.map((d) => (
            <li key={d.docId} className="flex flex-wrap items-center gap-2 py-2">
              <button
                type="button"
                onClick={() => void openDocument(wedId, d.docId)}
                className="min-w-[8rem] flex-1 text-left text-body underline underline-offset-4"
              >
                {d.filename}
              </button>
              <span className="tabular text-caption text-leaf-soft">
                {Math.max(1, Math.round(d.sizeBytes / 1024))} KB
              </span>
              <button
                type="button"
                onClick={() => setPendingRemove(d)}
                className="min-h-tap text-caption text-status-declinedFg underline underline-offset-4"
              >
                {t('common.remove')}
              </button>
            </li>
          ))}

          {unfinished.map((d) => (
            <li key={d.docId} className="flex flex-wrap items-center gap-2 py-2">
              <span className="min-w-[8rem] flex-1 text-body text-leaf-soft">{d.filename}</span>
              <span className="text-caption text-status-declinedFg">
                {t('documents.didNotFinish')}
              </span>
              <button
                type="button"
                onClick={() => remove.mutate({ docId: d.docId })}
                className="min-h-tap text-caption text-leaf-soft underline underline-offset-4"
              >
                {t('common.remove')}
              </button>
            </li>
          ))}
        </ul>
      )}

      {error && (
        <p role="alert" className="text-body text-status-declinedFg">
          {error}
        </p>
      )}

      <div>
        <Button
          type="button"
          variant="secondary"
          loading={upload.isPending}
          onClick={() => input.current?.click()}
        >
          {t('documents.add')}
        </Button>
        <input
          ref={input}
          type="file"
          accept={DOCUMENT_TYPES.join(',')}
          className="hidden"
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) void onPick(f);
          }}
        />
      </div>

      <ConfirmDialog
        open={pendingRemove !== undefined}
        title={t('documents.removeTitle', { name: pendingRemove?.filename ?? '' })}
        body={
          <>
            <p>{t('documents.removeAlso')}</p>
            <p>{t('common.cannotBeUndone')}</p>
          </>
        }
        confirmLabel={t('common.remove')}
        busy={remove.isPending}
        onConfirm={() => {
          if (pendingRemove) remove.mutate({ docId: pendingRemove.docId });
          setPendingRemove(undefined);
        }}
        onClose={() => setPendingRemove(undefined)}
      />
    </div>
  );
}
